import * as React from "react";
import { AnimatePresence, motion } from "motion/react";
import * as Popover from "@radix-ui/react-popover";
import { MOTION_DURATION_FAST } from "@sproutsocial/seeds-motion/unitless";
import type { TypePopoutProps } from "./PopoutTypes";
import styled from "styled-components";

const defaultAnimationConfig = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: {
    ease: "circOut",
    type: "tween",
    duration: process.env.NODE_ENV === "test" ? 0 : MOTION_DURATION_FAST,
  },
};

const StyledMotionDiv = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.container.background.base};
  border: ${({ theme }) => theme.borders[500]};
  border-color: ${({ theme }) => theme.colors.container.border.base};
  border-radius: ${({ theme }) => theme.radii[500]};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  padding: ${({ theme }) => theme.space[400]};
`;

export function Popout({
  children,
  content,
  open,
  defaultOpen,
  onOpenChange,
  animationConfig = defaultAnimationConfig,
  side = "bottom",
  sideOffset = 8,
  align = "center",
  alignOffset = 0,
  onOpenAutoFocus,
  onEscapeKeyDown,
  onCloseAutoFocus,
  onPointerDownOutside,
  onInteractOutside,
  zIndex = 8,
  ...rest
}: TypePopoutProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen ?? false);

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setIsOpen(newOpen);
      onOpenChange?.(newOpen);
    },
    [onOpenChange]
  );

  // Use controlled state if open prop is provided
  const isControlled = open !== undefined;
  const popoverOpen = isControlled ? open : isOpen;
  const popoverOnOpenChange = isControlled ? onOpenChange : handleOpenChange;

  // Handle ref forwarding for components that use innerRef instead of ref
  // (e.g., seeds-react-button)
  // Radix UI's asChild passes ref, but Button uses innerRef
  const radixRef = React.useRef<HTMLButtonElement | null>(null);

  return (
    <Popover.Root open={popoverOpen} onOpenChange={popoverOnOpenChange}>
      <Popover.Trigger ref={radixRef} asChild>
        {children}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          onOpenAutoFocus={onOpenAutoFocus}
          onEscapeKeyDown={onEscapeKeyDown}
          onCloseAutoFocus={onCloseAutoFocus}
          onPointerDownOutside={onPointerDownOutside}
          onInteractOutside={onInteractOutside}
          style={{ zIndex, position: "relative" }}
        >
          <StyledMotionDiv {...animationConfig} {...rest}>
            {content}
          </StyledMotionDiv>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
